import { ServiceRegistry } from '../../core/ServiceRegistry';

export interface WorldPoint { x: number; y: number }

/**
 * The local player's live world position, read from the running engine
 * (PlayerManager's physics entity: client-owned movement makes it the source
 * of truth).
 *
 * React's `player.position` does NOT follow movement. It holds the position
 * from load, a dev teleport or a server correction; server patches no longer
 * project x/y into it, because a new player object on every movement patch
 * re-rendered the whole `Game` tree at the patch rate (plan 13 §8). Code that
 * needs where the player stands right now reads it here, at the moment it
 * needs it. `fallback` (usually the React position) covers pre-game screens
 * and tests, where no engine is running.
 */
export function livePlayerPosition(fallback?: WorldPoint | null): WorldPoint {
  try {
    const registry = ServiceRegistry.getInstance();
    if (registry.has('EntitySystem')) {
      const position = registry.get('EntitySystem')?.getPlayerManager?.()?.getPlayer?.()?.position;
      if (Number.isFinite(position?.x) && Number.isFinite(position?.y)) {
        return { x: position!.x, y: position!.y };
      }
    }
  } catch { /* no engine: use the fallback */ }
  return { x: fallback?.x ?? 0, y: fallback?.y ?? 0 };
}

/** A copy of `character` carrying the live position (for scene restarts and saves). */
export function withLivePosition<T extends { position: WorldPoint }>(character: T): T {
  return { ...character, position: { ...character.position, ...livePlayerPosition(character.position) } };
}
